import Cookies from "js-cookie";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { useCustomerAnalytics } from "../../../services/hooks/useCustomerAnalytics";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../services/hooks/useTranslationI18";
import { setSelectedFranchise } from "../../../services/redux/slices/configSlice";
import {
  setBackPage,
  setCurrentDeliveryConfig,
} from "../../../services/redux/slices/deliverySlice";
import {
  setBottomSheetSource,
  setBottomSheetState,
} from "../../../services/redux/slices/globalComponentsToggleSlice";
import { setClickDetected } from "../../../services/redux/slices/locationSlice";
import { setQueryParam } from "../../../services/redux/slices/urlParamsSlice";
import { cn } from "../../../utils/cn";
import {
  COOKIE_ENUM,
  CUSTOMER_ANALYTICS_EVENT,
  DeliveryTypeDisplayName,
  OrderTypesEnum,
  PagesEnum,
} from "../../../utils/enums";
import { getFranchiseDisplayName } from "../../../utils/help/localization/localization";
import {
  getFranchiseSlug,
  getPageLink,
} from "../../../utils/help/navigation/navigation";
import { Restaurant } from "../../../utils/types";
import AutoDetectLocation from "../../global/AutoDetectLocation/AutoDetectLocation";
import { LangSwitch } from "../../global/LangSwitch/LangSwitch";
import styles from "./SelectOrderInfoV3.module.scss";
import {
  BeachDeliveryIcon,
  CabinDeliveryIcon,
  CarPickupIcon,
  DeliveryIcon,
  DineInIcon,
  OfficeDeliveryIcon,
  PickupIcon,
} from "./SelectOrderInfoV3Icons";
import { findFranchiseBySlug } from "../../../utils/help/franchise/findFranchiseBySlug";
import { selectCurrentDeliveryConfig } from "../../../services/redux/selectors/deliverySelectors";

type SelectOrderInfoV3Props = {
  restaurantInfo: Restaurant;
};

// "car_pickup" kept for backward compat; other delivery types use "delivery_type_{id}"
type OrderTypeSelection =
  | OrderTypesEnum
  | "car_pickup"
  | `delivery_type_${number}`;

// Welcome-drawer order-type selection analytics — only the delivery types with
// a dedicated event are tracked here (car pickup / beach / cabin).
function welcomeOrderTypeEvent(
  displayName: string
): CUSTOMER_ANALYTICS_EVENT | undefined {
  switch (displayName) {
    case DeliveryTypeDisplayName.CAR_PICKUP:
      return CUSTOMER_ANALYTICS_EVENT.CAR_PICK_UP_BUTTON;
    case DeliveryTypeDisplayName.BEACH_DELIVERY:
      return CUSTOMER_ANALYTICS_EVENT.BEACH_DELIVERY_BUTTON;
    case DeliveryTypeDisplayName.CABIN_DELIVERY:
      return CUSTOMER_ANALYTICS_EVENT.CABIN_DELIVERY_BUTTON;
    default:
      return undefined;
  }
}

function iconForDeliveryTypeName(displayName: string): JSX.Element {
  switch (displayName) {
    case DeliveryTypeDisplayName.CAR_PICKUP:
      return <CarPickupIcon />;
    case DeliveryTypeDisplayName.CABIN_DELIVERY:
      return <CabinDeliveryIcon />;
    case DeliveryTypeDisplayName.BEACH_DELIVERY:
      return <BeachDeliveryIcon />;
    case DeliveryTypeDisplayName.OFFICE_DELIVERY:
      return <OfficeDeliveryIcon />;
    default:
      return <DeliveryIcon />;
  }
}

export function SelectOrderInfoV3({ restaurantInfo }: SelectOrderInfoV3Props) {
  const { t } = useTranslationI18();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const addCustomerAnalytics = useCustomerAnalytics();

  const [selectedType, setSelectedType] = useState<
    OrderTypeSelection | undefined
  >(undefined);

  const activeFranchises = useAppSelector(
    (state) => state.config.activeFranchises
  );
  const selectedFranchise = useAppSelector(
    (state) => state.config.selectedFranchise
  );
  const deliveryV2Config = useAppSelector(selectCurrentDeliveryConfig);
  const orderTypeParamState = useAppSelector(
    (state) => state.queryParams.orderTypeParam
  );

  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  // Guards the one-time order-type restore below so it never re-fires over a
  // manual pick, while still allowing it to wait for Redux to hydrate.
  const hasRestoredType = useRef(false);

  // Scroll the selected order type button into view whenever selection changes
  useEffect(() => {
    if (selectedType) {
      buttonRefs.current[selectedType]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [selectedType]);

  // The branch shown in the drawer. A `?franchise=smart` deep link is NOT
  // committed to Redux until the user Proceeds (see BackgroundRequestsHelper),
  // so resolve it locally from the URL for display. The QR flow still commits
  // to Redux (legacy path), so prefer the committed franchise when present.
  const urlFranchiseParam = router.query.franchise as string | undefined;
  const urlFranchise = urlFranchiseParam
    ? findFranchiseBySlug(
        activeFranchises,
        getFranchiseSlug(urlFranchiseParam),
        selectedFranchise
      )
    : undefined;
  const currentBranch = selectedFranchise ?? urlFranchise;

  const showCurrentBranch = Boolean(currentBranch);

  // Order types shown must reflect what's actually orderable:
  // - a branch is selected → only that branch's order types
  // - no branch selected   → the union across all active franchises. The
  //   delivery-hub model resolves the branch AFTER the customer picks an order
  //   type (delivery auto-routes to whichever branch covers the address), so an
  //   order type available at *any* branch must be offered. Using the
  //   intersection here would hide delivery/dine-in whenever the branches don't
  //   all share them and break the hub.
  const orderTypeSourceFranchises = currentBranch
    ? [currentBranch]
    : activeFranchises;

  // Unique deliveryV2 delivery types across the source franchises (deduped by
  // id, keeping the first-seen displayName).
  const uniqueDeliveryTypes = Object.values(
    orderTypeSourceFranchises
      .flatMap((f) => f.franchiseConfig?.deliveryV2 ?? [])
      .reduce<Record<number, { id: number; displayName: string }>>(
        (acc, config) => {
          const { id, displayName } = config.deliveryType;
          if (!acc[id]) {
            acc[id] = { id, displayName };
          }

          return acc;
        },
        {}
      )
  );

  const hasTakeOut = orderTypeSourceFranchises.some(
    (f) => f.franchiseConfig?.takeOut
  );
  const hasDineIn = orderTypeSourceFranchises.some(
    (f) => f.franchiseConfig?.dineIn
  );

  /**
   * Static dine-in menu (prepits/project#4291): a guest arriving on the plain
   * URL still meets this drawer, and picking "Dine-in" there lands them on a
   * menu they cannot order from - so the button says what it actually does.
   *
   * `every`, not `some`: with no branch chosen yet the guest picks the order
   * type first, so the promise only holds if every dine-in branch on offer is
   * view-only. This renames THIS button alone - the order type is still
   * DINE_IN, and every other place that spells it out keeps saying Dine-in.
   */
  const dineInBranches = orderTypeSourceFranchises.filter(
    (f) => f.franchiseConfig?.dineIn
  );
  const dineInIsViewOnly =
    dineInBranches.length > 0 &&
    dineInBranches.every((f) => f.franchiseConfig?.dineInMenuView);

  // Restore the previously chosen order type from Redux — once. This does NOT
  // run only on mount: on a re-open right after Proceed, `orderTypeParam` and
  // especially `currentDeliveryConfig` can still be hydrating on the first
  // render, so it re-runs as those land (the ref keeps it to a single restore
  // and stops it overriding a manual pick). No prior order type → leave
  // unselected, even when a franchise is already known.
  useEffect(() => {
    if (hasRestoredType.current || !orderTypeParamState) {
      return;
    }

    if ((orderTypeParamState as string).startsWith("delivery_type_")) {
      setSelectedType(orderTypeParamState as unknown as OrderTypeSelection);
      hasRestoredType.current = true;
    } else if (orderTypeParamState === OrderTypesEnum.DINE_IN) {
      setSelectedType(OrderTypesEnum.DINE_IN);
      hasRestoredType.current = true;
    } else if (orderTypeParamState === OrderTypesEnum.TAKE_OUT) {
      setSelectedType(OrderTypesEnum.TAKE_OUT);
      hasRestoredType.current = true;
    } else if (orderTypeParamState === OrderTypesEnum.DELIVERYV2) {
      // Restore the exact delivery type the user confirmed from the committed
      // config. Crucially, do NOT guess the first delivery type when the config
      // hasn't hydrated yet — that's what preselected "Delivery" instead of the
      // real "Car Pickup". Wait instead: a later render (once the config lands)
      // re-runs this and restores correctly.
      const committedDeliveryType = deliveryV2Config?.deliveryType;
      if (committedDeliveryType) {
        setSelectedType(
          committedDeliveryType.displayName ===
            DeliveryTypeDisplayName.CAR_PICKUP
            ? "car_pickup"
            : (`delivery_type_${committedDeliveryType.id}` as OrderTypeSelection)
        );
        hasRestoredType.current = true;
      }
    }
  }, [orderTypeParamState, deliveryV2Config?.deliveryType?.id]); // eslint-disable-line

  const closeBottomSheet = () => {
    dispatch(setBottomSheetState(false));
    dispatch(setBottomSheetSource(null));
  };

  const handleSkip = () => {
    if (deliveryV2Config) {
      dispatch(setCurrentDeliveryConfig(deliveryV2Config));
    }
    closeBottomSheet();
  };

  const handleProceed = () => {
    if (!selectedType) {
      return;
    }

    const isDeliveryType =
      selectedType === "car_pickup" ||
      selectedType.startsWith("delivery_type_");

    // Persist delivery type so the location screen opens on the right tab and
    // so order submission has the correct franchiseDeliveryConfigId.
    const matchingConfig = isDeliveryType
      ? activeFranchises
          .flatMap((f) => f.franchiseConfig?.deliveryV2 ?? [])
          .find((c) =>
            selectedType === "car_pickup"
              ? c.deliveryType.displayName ===
                DeliveryTypeDisplayName.CAR_PICKUP
              : c.deliveryType.id ===
                Number((selectedType as string).replace("delivery_type_", ""))
          )
      : undefined;

    if (matchingConfig) {
      dispatch(setCurrentDeliveryConfig(matchingConfig));
    }

    // Only table-delivery (and the legacy V1 delivery flag) actually needs
    // the map/address flow. If the franchise is already known (QR/table
    // flow — see "you are currently at" above) and the chosen type doesn't
    // need a map, there's nothing left to pick — commit it directly instead
    // of sending the user to the Location screen.
    const needsLocationScreen =
      selectedType === OrderTypesEnum.DELIVERYV2 ||
      matchingConfig?.deliveryType.displayName ===
        DeliveryTypeDisplayName.TABLE_DELIVERY;

    if (currentBranch && !needsLocationScreen) {
      const orderType = isDeliveryType
        ? OrderTypesEnum.DELIVERYV2
        : (selectedType as OrderTypesEnum);

      // Prevents useFranchiseData from re-opening this drawer on a later
      // Home mount — it treats a missing cookie as "no franchise ever
      // confirmed".
      const chooseDate = new Date();
      chooseDate.setHours(chooseDate.getHours() + 2);
      Cookies.set(
        COOKIE_ENUM.LAST_SELECTED_FRANCHISE,
        // slug can be null on the slug-less main franchise; fall back to id so
        // .toString() never throws (the cookie is only checked for existence).
        String(currentBranch.slug ?? currentBranch.id),
        { expires: chooseDate }
      );

      addCustomerAnalytics(
        CUSTOMER_ANALYTICS_EVENT.CONFIRM_BRANCH_AND_ORDER_TYPE,
        {
          franchiseId: currentBranch.id,
          franchiseSlug: currentBranch.slug,
          orderType,
        },
        currentBranch.id,
        currentBranch.slug
      );

      // Commit the branch + order type together — this is the point at which
      // the URL-local franchise becomes a real selection in Redux.
      dispatch(setSelectedFranchise(currentBranch));
      dispatch(setQueryParam(orderType));
      dispatch(setClickDetected(true));

      // Same-page navigation (still Home) — this keeps the URL's
      // franchise/order_type query params in sync with redux, which other
      // Home-mounted logic (e.g. useFranchiseFiltering) depends on.
      const link = getPageLink(router, PagesEnum.Home, {
        franchiseSlug: currentBranch.slug,
        orderType,
      });

      // Close AFTER the navigation settles. Closing first unmounted the drawer
      // while router.push was still in flight, so the menu showed through for
      // ~500ms and the drawer then appeared to re-open on the new screen
      // (prepits/project#3913 UX case 5). .finally so a rejected or cancelled
      // navigation can't strand the drawer open.
      router.push(link.href, link.as).finally(closeBottomSheet);

      return;
    }

    dispatch(setBackPage(PagesEnum.Home));

    const locationLink = getPageLink(router, PagesEnum.Location, {
      orderType: isDeliveryType
        ? OrderTypesEnum.DELIVERYV2
        : (selectedType as unknown as OrderTypesEnum),
    });
    // Closed after the push settles — see the note on the branch above.
    router.push(locationLink.href, locationLink.as).finally(closeBottomSheet);
  };

  // One button per unique delivery type from deliveryV2, plus takeOut and dineIn
  const ORDER_TYPE_BUTTONS: {
    type: OrderTypeSelection;
    label: string;
    icon: JSX.Element;
    testId: string;
    analyticsEvent?: CUSTOMER_ANALYTICS_EVENT;
  }[] = [
    // Dynamic delivery type buttons (one per unique deliveryType in deliveryV2)
    ...uniqueDeliveryTypes.map(({ id, displayName }) => ({
      type: (displayName === DeliveryTypeDisplayName.CAR_PICKUP
        ? "car_pickup"
        : `delivery_type_${id}`) as OrderTypeSelection,
      label: t(`table-page:${displayName.toLowerCase()}`),
      icon: iconForDeliveryTypeName(displayName),
      // displayName-based (stable across environments); DB ids differ per env.
      testId: `order-type-btn-${displayName}`,
      analyticsEvent: welcomeOrderTypeEvent(displayName),
    })),
    // Legacy delivery V1 (if no deliveryV2 configured)
    ...(uniqueDeliveryTypes.length === 0 &&
    orderTypeSourceFranchises.some((f) => f.franchiseConfig?.delivery)
      ? [
          {
            type: OrderTypesEnum.DELIVERYV2 as OrderTypeSelection,
            label: t("table-page:table-delivery"),
            icon: <DeliveryIcon />,
            testId: "order-type-btn-delivery",
          },
        ]
      : []),
    // Takeout
    ...(hasTakeOut
      ? [
          {
            type: OrderTypesEnum.TAKE_OUT as OrderTypeSelection,
            label: t("table-page:table-take-out"),
            icon: <PickupIcon />,
            testId: "order-type-btn-pickup",
          },
        ]
      : []),
    // Dine in
    ...(hasDineIn
      ? [
          {
            type: OrderTypesEnum.DINE_IN as OrderTypeSelection,
            label: dineInIsViewOnly
              ? t("table-page:table-view-menu")
              : t("table-page:table-dine-in"),
            icon: <DineInIcon />,
            testId: "order-type-btn-dine-in",
          },
        ]
      : []),
  ];

  return (
    <div className={styles.container} data-testid="select-order-info-v3-drawer">
      {/* V2 mounted this inside LocationDropdown, which the V3 drawer does not
          render — so `autoLocationDetection` silently stopped working for every
          V3 restaurant. Renders null; it only drives the geolocation effect. */}
      {restaurantInfo.config?.autoLocationDetection && (
        <AutoDetectLocation page={router.pathname.split("/")[1] as PagesEnum} />
      )}
      <div className={styles.lang_div}>
        <LangSwitch
          preferredCountry={restaurantInfo.preferredCountry}
          onToggle={(lang) => {
            if (lang === "en") {
              addCustomerAnalytics(
                CUSTOMER_ANALYTICS_EVENT.SWITCH_TO_ENGLISH_FROM_WELCOME
              );
            } else if (lang?.startsWith("ar")) {
              addCustomerAnalytics(
                CUSTOMER_ANALYTICS_EVENT.SWITCH_TO_ARABIC_FROM_WELCOME
              );
            }
          }}
        />
      </div>

      {showCurrentBranch && currentBranch && (
        <div className={styles.current_branch_section}>
          <p className={styles.current_branch_label}>
            {t("common:you-are-currently-at")}
          </p>
          <div className={styles.current_branch_box}>
            <span className={styles.current_branch_name}>
              {getFranchiseDisplayName(currentBranch)}
            </span>
            <span className={styles.current_branch_icons}>
              {currentBranch.franchiseConfig?.deliveryV2?.some(
                (d) =>
                  d.deliveryType.displayName !==
                  DeliveryTypeDisplayName.CAR_PICKUP
              ) && <DeliveryIcon />}
              {currentBranch.franchiseConfig?.takeOut && <PickupIcon />}
              {currentBranch.franchiseConfig?.dineIn && <DineInIcon />}
              {currentBranch.franchiseConfig?.deliveryV2?.some(
                (d) =>
                  d.deliveryType.displayName ===
                  DeliveryTypeDisplayName.CAR_PICKUP
              ) && <CarPickupIcon />}
            </span>
          </div>
        </div>
      )}

      <h3 className={styles.section_title}>{t("common:select-order-type")}</h3>

      <div className={styles.order_type_row}>
        {ORDER_TYPE_BUTTONS.map(
          ({ type, label, icon, testId, analyticsEvent }) => (
            <button
              key={type}
              ref={(el) => {
                buttonRefs.current[type] = el;
              }}
              className={cn(
                styles.order_type_btn,
                selectedType === type && styles.selected
              )}
              onClick={() => {
                setSelectedType(type);
                if (analyticsEvent) {
                  addCustomerAnalytics(analyticsEvent);
                }
              }}
              data-testid={testId}
            >
              <span className={styles.order_type_icon_circle}>{icon}</span>
              <span className={styles.order_type_label}>{label}</span>
            </button>
          )
        )}
      </div>

      <div className={styles.button_container}>
        <button
          className={cn(styles.button, styles.btn_cancel)}
          onClick={handleSkip}
          data-testid="select-order-info-v3-skip-button"
        >
          {t("common:skip")}
        </button>
        <button
          className={cn(
            styles.button,
            styles.btn_save,
            !selectedType && styles.btn_disabled
          )}
          onClick={handleProceed}
          disabled={!selectedType}
          data-testid="select-order-info-v3-proceed-button"
        >
          {t("common:proceed")}
        </button>
      </div>
    </div>
  );
}
